import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building2, Lock, User } from 'lucide-react';
import BASE_URL from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: username, password }),
      });

      const result = await res.json();

      if (!res.ok) {
        setError(result.message || 'Invalid username or password');
        return;
      }

      const { data } = result;

      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userName', data.name);
      localStorage.setItem('username', data.username);
      localStorage.setItem('userRole', data.role || '');
      localStorage.setItem('portals', JSON.stringify(data.portals || []));

      navigate('/my-apps');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Logo & Title */}


        {/* Heading */}
        <div className="mb-10 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-3 bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] bg-clip-text text-transparent">
            ADTW Management Portal
          </h2>
          <p className="text-gray-600 text-lg">Single Sign-On for all ADTW Portals</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Username Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2.5">
              Email Id
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400 group-focus-within:text-[#3d82c4] transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Enter your username or email"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value.trim());
                  setError('');
                }}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 focus:outline-none focus:border-[#3d82c4] transition-all text-gray-800 placeholder-gray-400 bg-white/80 backdrop-blur-sm hover:bg-white"
                required
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2.5">
              Password
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-[#3d82c4] transition-colors" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 focus:outline-none focus:border-[#3d82c4] transition-all text-gray-800 placeholder-gray-400 bg-white/80 backdrop-blur-sm hover:bg-white"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-[#3d82c4] transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] hover:from-[#2d6fad] hover:to-[#1d5a9d] text-white font-semibold py-4 transition-all duration-300 shadow-lg hover:shadow-2xl disabled:opacity-70 disabled:cursor-not-allowed transform hover:-translate-y-0.5 mt-8"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Signing in...</span>
              </div>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-12">
          © {new Date().getFullYear()} K Labs Technology. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;